import { getStartingSimulationHorizon } from "@client/pages/round/Round";
import {
  getActiveEvaluationIds,
  getActiveEvaluatorEvaluations,
  getActiveProposals,
  getActiveRecommenders,
  getEvaluableRequestMax,
  getEvaluatorTrancheList,
  getProposalOrPlaceholder,
} from "@engine/round/accessors";
import { getEvaluableIndicesMap } from "@lib/algoInterface/getEvaluableIndicesMap";
import {
  runSProcessOnRound,
  SProcessOutput,
} from "@lib/algoInterface/runSProcessOnRound";
import { trancheListToAlgoTranches } from "@lib/algoInterface/trancheListToAlgoInterface";
import { sortByMultipleProperties } from "@lib/utils/sortByMultipleProperties";
import { unilateralAlloc } from "algorithm/unilateralAlloc";
import { Proposal, Recommender, RoundAggregate } from "engine/round/aggregate";

type UnilateralAllocationsByProposal = {
  [proposalId: string]: { [key: string]: number | undefined };
};

function getUnilateralAllocationsByProposal(
  round: RoundAggregate,
  recommenders: Recommender[],
): UnilateralAllocationsByProposal {
  const allocations: UnilateralAllocationsByProposal = {};

  let results: SProcessOutput | Error;
  try {
    results = runSProcessOnRound(round);
  } catch {
    // TODO: handle error
    return {};
  }

  const lastLayerIndex = results.layerTargetTotals.length - 1;

  // skip hold for now
  for (let i = 1; i < results.layerTargetTotals[lastLayerIndex].length; i++) {
    const allocation = results.layerTargetTotals[lastLayerIndex].get(i);
    const proposal = results.layerNodes[lastLayerIndex][i] as Proposal;
    allocations[proposal.id] = { sigma: allocation };
  }

  // final hold value is the total of all hold nodes
  let holdTotal = 0;
  for (let i = 0; i < results.layerTargetTotals.length; i++) {
    holdTotal += results.layerTargetTotals[i].get(0);
  }

  allocations[round.holdProposalId] = { sigma: holdTotal };

  const proposals = getActiveProposals(round);
  const proposalMax: { [evaluableId: string]: number | null } = {};
  for (const proposal of proposals) {
    proposalMax[proposal.id] = getEvaluableRequestMax(round, proposal.id);
  }

  const simulationHorizon = getStartingSimulationHorizon(round);
  for (const recommender of recommenders) {
    const evaluations = getActiveEvaluatorEvaluations(round, recommender);

    const evaluableIds = evaluations.map(
      (evaluation) => evaluation.evaluableId,
    );
    const evaluableIndicesMap = getEvaluableIndicesMap(
      evaluableIds,
      round.holdProposalId,
    );

    const evaluatorTrancheList = getEvaluatorTrancheList(round, recommender);

    const tranches = trancheListToAlgoTranches(
      evaluatorTrancheList,
      evaluableIndicesMap,
      proposalMax,
      new Set(evaluableIds),
    );

    const unilateralAllocations = unilateralAlloc(tranches, simulationHorizon);
    for (const [evaluableId, index] of Object.entries(evaluableIndicesMap)) {
      const allocation = unilateralAllocations.get(index);
      allocations[evaluableId][recommender.id] = allocation;
    }
  }

  return allocations;
}

export type DisagreementsMatrixData = {
  rows: DisagreementsMatrixRow[];
  keys: string[]; // Recommender ID or the string "sigma"
  keyNames: { [key: string]: string };
};

export type DisagreementsMatrixRow = {
  proposal: Proposal;
  sections: { [key: string]: DisagreementsMatrixRowSection };
};

type DisagreementsMatrixRowSection = {
  key: string;
  values: { [key: string]: number | undefined };
};

export function calculateDisagreementsMatrixData(
  round: RoundAggregate,
): DisagreementsMatrixData {
  const recommenders = getActiveRecommenders(round);
  recommenders.sort(
    sortByMultipleProperties("meanRecommenderThreshold", "shortname"),
  );

  const keys = ["sigma", "total"];
  const keyNames: { [key: string]: string } = {
    sigma: "Σ",
    total: "Tot",
  };
  for (const recommender of recommenders) {
    keyNames[recommender.id] = recommender.shortname;
    keys.push(recommender.id);
  }

  const allocationsByProposal = getUnilateralAllocationsByProposal(
    round,
    recommenders,
  );
  const activeProposalIds = getActiveEvaluationIds(recommenders);

  const rows: DisagreementsMatrixRow[] = [];
  for (const proposalId of activeProposalIds) {
    if (!allocationsByProposal[proposalId]) {
      console.error("No allocation for active proposal ", proposalId);
      continue;
    }

    const proposal = getProposalOrPlaceholder(round, proposalId);
    const sections: DisagreementsMatrixRow["sections"] = {};

    for (const sectionKey of keys) {
      if (sectionKey === "total") {
        continue;
      }

      sections[sectionKey] = { key: sectionKey, values: {} };

      let sectionAbsTotal = 0;
      for (const colKey of keys) {
        if (colKey === sectionKey || colKey === "total") {
          continue;
        }

        const sectionAllocation = allocationsByProposal[proposalId][sectionKey];
        const columnAllocation = allocationsByProposal[proposalId][colKey];

        if (sectionAllocation === undefined || columnAllocation === undefined) {
          continue;
        }

        const disagreement = Math.round(columnAllocation - sectionAllocation);
        sectionAbsTotal += Math.abs(disagreement);

        sections[sectionKey].values[colKey] = disagreement;
      }

      sections[sectionKey].values["total"] = sectionAbsTotal;
    }

    rows.push({ proposal, sections });
  }

  rows.sort((a, b) => a.proposal.shortname.localeCompare(b.proposal.shortname));

  return { keys, rows, keyNames };
}
